import { useMemo } from 'react';

import { buildOverflowMenu } from '@/components/molecules/buildOverflowMenu';
import { type NavItem, type NavMenu } from '@/config/navigation';

export type HeaderNavItems = {
  /** The items that still have room on the row, in order. */
  inline: readonly NavItem[];
  /** The "More" menu holding the rest, or null when everything fits. */
  overflowMenu: NavMenu | null;
};

/**
 * Splits the primary nav at the point `useHeaderFit` decided on.
 *
 * Small enough to be two `slice`s inline, and separate anyway for one reason
 * worth the file: `buildOverflowMenu` returns a fresh object every time it is
 * called, and `NavMenuButton` holds open/focus state keyed to the menu it was
 * given. Rebuilding that object on every scroll tick — and the header
 * re-renders on every scroll tick, because `useScrollTrigger` drives it —
 * would hand the pill a new `menu` prop sixty times a second while a visitor
 * has the panel open under their pointer.
 *
 * Memoised on the identity of the item list and the split point, both of
 * which change only when the row is actually replanned.
 */
export function useHeaderNavItems(items: readonly NavItem[], visibleCount: number): HeaderNavItems {
  return useMemo(() => {
    const folded = items.slice(visibleCount);
    return {
      inline: items.slice(0, visibleCount),
      overflowMenu: folded.length > 0 ? buildOverflowMenu(folded) : null,
    };
  }, [items, visibleCount]);
}
